import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import moment from 'moment'

import routes from '../../routers'
import Layout from '../../components/Layout'
import { ArrowBack } from '../../components/SvgIcon'
import { apiError } from '../../api/account'
import { getGift, GIFT_STATUS_TEXT } from '../../api/gifts'

import '../PurchaseBook/styles.css'
import styles from '../Gift/Gift.module.css'

const cap = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : '')
const dollars = (n) => (n === null || n === undefined ? '' : `$${Number(n).toFixed(2)}`)
const day = (d) => (d ? moment(d).format('MMMM D, YYYY') : '')

// One gift the buyer sent: just paid, scheduled, sent or opened
const GiftSent = () => {
  const { giftId } = useParams()
  const { search } = useLocation()
  const justPaid = new URLSearchParams(search).has('paid')
  const [gift, setGift] = useState(null)
  const [error, setError] = useState('')
  const tries = useRef(0)
  const timer = useRef(null)

  const load = () =>
    getGift(giftId)
      .then(({ data }) => {
        setGift(data)
        // Just back from payment: the confirmation can take a few seconds
        if (data.status === 'pending_payment' && tries.current < 20) {
          tries.current += 1
          timer.current = setTimeout(load, 3000)
        }
      })
      .catch((e) => setError(apiError(e, 'We could not load this gift.')))

  useEffect(() => {
    load()
    return () => clearTimeout(timer.current)
  }, [giftId])

  let body
  if (error && !gift) body = <p className="cbError">{error}</p>
  else if (!gift) body = <p className="cbMuted">Loading…</p>
  else {
    const scheduled = gift.status === 'paid'
    body = (
      <section className="cbSection">
        {justPaid && gift.status !== 'pending_payment' ? (
          <p className={styles.thanks}>Thank you! Your gift is paid. We sent you a receipt.</p>
        ) : null}
        <div className="cbSectionHead">
          <div>
            <h2 className={styles.big}>
              {cap(gift.what)} for {gift.recipient_name}
            </h2>
            <p className="cbMuted">{gift.recipient_email}</p>
          </div>
          <span className={styles.pill}>{GIFT_STATUS_TEXT[gift.status] || gift.status}</span>
        </div>
        {gift.status === 'pending_payment' ? (
          <p className="cbMuted">
            Checking your payment… If you closed the payment page, nothing was charged.
          </p>
        ) : null}
        {scheduled ? (
          <p>
            We’ll email the gift to {gift.recipient_name} on <b>{day(gift.deliver_on)}</b>.
          </p>
        ) : null}
        {gift.status === 'delivered' ? (
          <p>
            We emailed it to {gift.recipient_name} on {day(gift.delivered_at)}. It works until{' '}
            {day(gift.expires_at)}.
          </p>
        ) : null}
        {['redeemed', 'used'].includes(gift.status) ? (
          <p>
            {gift.recipient_name} opened the gift on {day(gift.redeemed_at)}.
          </p>
        ) : null}
        {gift.message ? (
          <p className={styles.thanks} style={{ whiteSpace: 'pre-line' }}>
            {gift.message}
          </p>
        ) : null}
        {gift.total !== null ? (
          <p className="cbMuted">
            Paid {dollars(gift.total)}, code ending {gift.code_hint}.
          </p>
        ) : null}
        <p className="cbMuted">
          Need to change the email address or the day? Write to{' '}
          <a className="cbTextLink" href="mailto:info@getmorsel.com">
            info@getmorsel.com
          </a>
          .
        </p>
      </section>
    )
  }

  return (
    <Layout title="Your gift">
      <div className={`cbPage ${styles.page}`}>
        <div className="cbIntro">
          <div>
            <Link className="cbBack" to={routes.gift}>
              <ArrowBack />
              Gifts
            </Link>
            <h1 className="cbTitle">Your gift</h1>
          </div>
        </div>
        {body}
      </div>
    </Layout>
  )
}

export default GiftSent
